import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Upload, Sparkles, FileText, Image, Loader2, CheckCircle, Edit, Send, X, Users, Eye, Brain, Star, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssignmentUploadManager = () => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [questionPaper, setQuestionPaper] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeView, setActiveView] = useState('upload');
  const [savedPapers, setSavedPapers] = useState([]);
  const [viewPaper, setViewPaper] = useState(null);
  const [reviewSubmission, setReviewSubmission] = useState(null);
  const [reviewForm, setReviewForm] = useState({ feedback: '', final_total: 0, send_to_student: true, send_to_parent: true });
  const [submittingReview, setSubmittingReview] = useState(false);

  const [submitForm, setSubmitForm] = useState({ title: '', description: '', subject: '', grade_level: '', due_date: '' });

  const fetchSavedPapers = useCallback(async () => {
    try { const res = await axios.get(`${API}/teacher/uploaded-papers`); setSavedPapers(res.data.papers || []); } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchSavedPapers(); }, [fetchSavedPapers]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(file.type)) { toast.error('Only JPEG, PNG, WebP images and PDF files are allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File size must be under 10MB'); return; }
    setUploading(true); setQuestionPaper(null);
    try {
      const formData = new FormData(); formData.append('file', file);
      const res = await axios.post(`${API}/teacher/assignments/upload-file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadedFile({ id: res.data.file_id, name: res.data.filename, size: res.data.size, type: file.type });
      toast.success('File uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleAIConvert = async () => {
    if (!uploadedFile) return; setConverting(true);
    try {
      const formData = new FormData(); formData.append('file_id', uploadedFile.id);
      const res = await axios.post(`${API}/teacher/assignments/convert-to-questions`, formData);
      setQuestionPaper(res.data.question_paper); setEditMode(false);
      const qp = res.data.question_paper;
      setSubmitForm(prev => ({ ...prev, title: qp.title || prev.title, subject: qp.subject || prev.subject }));
      toast.success('Question paper extracted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Conversion failed'); }
    finally { setConverting(false); }
  };

  const updateQuestion = (sIdx, qIdx, field, value) => { const u = { ...questionPaper }; u.sections[sIdx].questions[qIdx][field] = value; setQuestionPaper(u); };
  const updateSection = (sIdx, field, value) => { const u = { ...questionPaper }; u.sections[sIdx][field] = value; setQuestionPaper(u); };
  const removeQuestion = (sIdx, qIdx) => { const u = { ...questionPaper }; u.sections[sIdx].questions.splice(qIdx, 1); setQuestionPaper({ ...u }); };
  const addQuestion = (sIdx) => { const u = { ...questionPaper }; u.sections[sIdx].questions.push({ number: u.sections[sIdx].questions.length + 1, type: 'mcq', text: '', options: ['A) ', 'B) ', 'C) ', 'D) '], correct_answer: 'A', marks: u.sections[sIdx].marks_per_question || 1 }); setQuestionPaper({ ...u }); };

  const handleSubmitAssignment = async () => {
    if (!submitForm.title || !submitForm.subject || !submitForm.grade_level || !submitForm.due_date) { toast.error('Fill all required fields'); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', submitForm.title); formData.append('description', submitForm.description);
      formData.append('subject', submitForm.subject); formData.append('grade_level', submitForm.grade_level);
      formData.append('due_date', submitForm.due_date); formData.append('question_paper_json', JSON.stringify(questionPaper));
      formData.append('source_file_id', uploadedFile?.id || '');
      const res = await axios.post(`${API}/teacher/assignments/create-from-paper`, formData);
      toast.success(res.data.message || 'Assignment created!');
      setShowSubmitDialog(false); handleReset(); fetchSavedPapers(); setActiveView('papers');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleReset = () => { setUploadedFile(null); setQuestionPaper(null); setEditMode(false); setSubmitForm({ title: '', description: '', subject: '', grade_level: '', due_date: '' }); };

  const handleReviewSubmit = async () => {
    if (!reviewSubmission) return;
    setSubmittingReview(true);
    try {
      await axios.put(`${API}/teacher/assignment/${reviewSubmission.assignment_id}/submission/${reviewSubmission.student_id}/review`, {
        feedback: reviewForm.feedback, final_total: reviewForm.final_total,
        send_to_student: reviewForm.send_to_student, send_to_parent: reviewForm.send_to_parent
      });
      toast.success('Review submitted and feedback sent');
      setReviewSubmission(null); fetchSavedPapers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmittingReview(false); }
  };

  const openReview = (paper, sub) => {
    setReviewSubmission({ ...sub, assignment_id: paper.id, assignment_title: paper.title, total_marks: paper.total_marks });
    setReviewForm({ feedback: '', final_total: sub.earned_marks ?? sub.score_percentage ?? 0, send_to_student: true, send_to_parent: true });
  };

  return (
    <div className="space-y-6" data-testid="assignment-upload-manager">
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList className="mb-4">
          <TabsTrigger value="upload" data-testid="upload-new-tab"><Upload className="w-4 h-4 mr-1" /> Upload New</TabsTrigger>
          <TabsTrigger value="papers" data-testid="saved-papers-tab"><FileText className="w-4 h-4 mr-1" /> My Papers ({savedPapers.length})</TabsTrigger>
        </TabsList>

        {/* UPLOAD TAB */}
        <TabsContent value="upload" className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4"><div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">1</div><h3 className="font-semibold text-gray-800">Upload Question Paper</h3></div>
            {!uploadedFile ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-all" onClick={() => fileInputRef.current?.click()} data-testid="upload-dropzone">
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={handleFileSelect} />
                {uploading ? <div className="flex flex-col items-center gap-2"><Loader2 className="w-10 h-10 animate-spin text-blue-500" /><p className="text-gray-500">Uploading...</p></div>
                : <div className="flex flex-col items-center gap-2"><Upload className="w-10 h-10 text-gray-400" /><p className="text-gray-600 font-medium">Click to upload image or PDF</p><p className="text-xs text-gray-400">JPEG, PNG, WebP, PDF — Max 10MB</p></div>}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div className="flex-1"><div className="flex items-center gap-2">{uploadedFile.type?.includes('pdf') ? <FileText className="w-4 h-4 text-red-500" /> : <Image className="w-4 h-4 text-blue-500" />}<span className="font-medium text-sm">{uploadedFile.name}</span></div><span className="text-xs text-gray-500">{(uploadedFile.size / 1024).toFixed(1)} KB</span></div>
                <Button size="sm" variant="ghost" className="text-gray-400" onClick={handleReset}><X className="w-4 h-4" /></Button>
              </div>
            )}
          </Card>

          {uploadedFile && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4"><div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-sm font-bold">2</div><h3 className="font-semibold text-gray-800">Convert to Question Paper</h3></div>
              {!questionPaper ? (
                <div className="text-center py-4">
                  <Button data-testid="ai-convert-btn" onClick={handleAIConvert} disabled={converting} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6">
                    {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}{converting ? 'Analyzing...' : 'AI Convert to Questions'}
                  </Button>
                </div>
              ) : (
                <div className="flex justify-between items-center"><p className="text-sm text-green-600 font-medium flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Conversion complete</p><Button size="sm" variant="outline" onClick={() => setEditMode(!editMode)} data-testid="toggle-edit-btn"><Edit className="w-4 h-4 mr-1" /> {editMode ? 'Done' : 'Edit'}</Button></div>
              )}
            </Card>
          )}

          {questionPaper && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4"><div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-bold">3</div><h3 className="font-semibold text-gray-800">Review & Edit</h3></div>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-gray-500">Title:</span> <span className="font-medium">{questionPaper.title || 'Untitled'}</span></div>
                    <div><span className="text-gray-500">Subject:</span> <span className="font-medium">{questionPaper.subject || 'N/A'}</span></div>
                    <div><span className="text-gray-500">Total Marks:</span> <span className="font-medium">{questionPaper.total_marks || 'N/A'}</span></div>
                    <div><span className="text-gray-500">Duration:</span> <span className="font-medium">{questionPaper.duration || 'N/A'}</span></div>
                  </div>
                </div>
                {(questionPaper.sections || []).map((section, sIdx) => (
                  <div key={sIdx} className="border rounded-lg overflow-hidden" data-testid={`section-${sIdx}`}>
                    <div className="bg-blue-50 px-4 py-2 flex items-center justify-between">
                      {editMode ? <Input className="bg-white w-48 h-8 text-sm" value={section.name} onChange={e => updateSection(sIdx, 'name', e.target.value)} /> : <span className="font-semibold text-blue-800 text-sm">{section.name}</span>}
                      <div className="flex items-center gap-2"><Badge variant="outline" className="text-xs">{section.questions?.length || 0} Q</Badge>{editMode && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addQuestion(sIdx)}>+ Q</Button>}</div>
                    </div>
                    <div className="p-3 space-y-3">
                      {(section.questions || []).map((q, qIdx) => (
                        <div key={qIdx} className="p-3 bg-gray-50 rounded-lg text-sm" data-testid={`question-${sIdx}-${qIdx}`}>
                          <div className="flex items-start gap-2">
                            <span className="text-gray-400 font-mono text-xs mt-1">Q{q.number || qIdx + 1}</span>
                            <div className="flex-1">
                              {editMode ? <Textarea className="bg-white text-sm min-h-[60px]" value={q.text} onChange={e => updateQuestion(sIdx, qIdx, 'text', e.target.value)} /> : <p className="text-gray-800">{q.text}</p>}
                              {q.options?.length > 0 && <div className="mt-2 space-y-1">{q.options.map((opt, oIdx) => <div key={oIdx}>{editMode ? <Input className="bg-white h-7 text-xs" value={opt} onChange={e => { const n = [...q.options]; n[oIdx] = e.target.value; updateQuestion(sIdx, qIdx, 'options', n); }} /> : <span className={`text-xs ${q.correct_answer && opt.startsWith(q.correct_answer) ? 'text-green-600 font-medium' : 'text-gray-600'}`}>{opt}</span>}</div>)}</div>}
                              <div className="flex items-center gap-3 mt-2 text-xs text-gray-400"><Badge variant="outline" className="text-xs">{q.type || 'mcq'}</Badge><span>{q.marks || 1} marks</span>{q.correct_answer && <span className="text-green-600">Ans: {q.correct_answer}</span>}</div>
                            </div>
                            {editMode && <Button size="sm" variant="ghost" className="text-red-400 h-7 w-7 p-0" onClick={() => removeQuestion(sIdx, qIdx)}><X className="w-3 h-3" /></Button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end"><Button data-testid="submit-assignment-btn" onClick={() => setShowSubmitDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 px-6"><Send className="w-4 h-4 mr-2" /> Assign to Students</Button></div>
            </Card>
          )}
        </TabsContent>

        {/* SAVED PAPERS TAB */}
        <TabsContent value="papers" className="space-y-4">
          {savedPapers.length === 0 ? (
            <Card className="p-12 text-center"><FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">No uploaded papers yet</p></Card>
          ) : savedPapers.map(paper => (
            <Card key={paper.id} className="p-5 border-0 shadow-lg" data-testid={`paper-${paper.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{paper.title}</h3>
                    <Badge variant="outline">{paper.subject}</Badge>
                    <Badge className="bg-blue-100 text-blue-700">Grade {paper.grade_level}</Badge>
                    <Badge className="bg-green-100 text-green-700">{paper.total_marks || '?'} marks</Badge>
                  </div>
                  <p className="text-xs text-gray-500">Created: {new Date(paper.created_at).toLocaleDateString()} | Due: {paper.due_date ? new Date(paper.due_date).toLocaleDateString() : 'N/A'}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => setViewPaper(paper)} data-testid={`view-paper-${paper.id}`}><Eye className="w-4 h-4 mr-1" /> View Paper</Button>
              </div>

              {/* Assigned Students */}
              <div className="mb-3">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1"><Users className="w-3 h-3" /> Assigned to ({paper.assigned_student_details?.length || paper.assigned_students?.length || 0} students):</p>
                <div className="flex flex-wrap gap-1">{(paper.assigned_student_details || []).map((s, i) => <Badge key={i} variant="outline" className="text-xs">{s.name}</Badge>)}</div>
              </div>

              {/* Submissions */}
              {paper.submissions_count > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">Submissions ({paper.submissions_count}):</p>
                  <div className="space-y-2">
                    {paper.submissions_summary?.map((sub, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{sub.student_name || 'Student'}</span>
                          <Badge className={`text-xs ${(sub.score_percentage || 0) >= 70 ? 'bg-green-100 text-green-700' : (sub.score_percentage || 0) >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                            {sub.earned_marks ?? 0}/{paper.total_marks || 0} ({sub.score_percentage ?? 0}%)
                          </Badge>
                          {sub.teacher_reviewed && <Badge className="bg-indigo-100 text-indigo-700 text-xs"><CheckCircle className="w-3 h-3 mr-0.5" />Reviewed</Badge>}
                        </div>
                        {!sub.teacher_reviewed && (
                          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => openReview(paper, sub)} data-testid={`review-sub-${sub.student_id}`}>
                            <Star className="w-3 h-3 mr-1" /> Review & Grade
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Assignment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input data-testid="assign-title-input" value={submitForm.title} onChange={e => setSubmitForm(p => ({ ...p, title: e.target.value }))} placeholder="Assignment title" /></div>
            <div><Label>Description</Label><Textarea value={submitForm.description} onChange={e => setSubmitForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description..." rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Subject *</Label><Input data-testid="assign-subject-input" value={submitForm.subject} onChange={e => setSubmitForm(p => ({ ...p, subject: e.target.value }))} placeholder="e.g. Mathematics" /></div>
              <div><Label>Grade Level *</Label><Input data-testid="assign-grade-input" value={submitForm.grade_level} onChange={e => setSubmitForm(p => ({ ...p, grade_level: e.target.value }))} placeholder="e.g. 10th" /></div>
            </div>
            <div><Label>Due Date *</Label><Input data-testid="assign-due-date-input" type="date" value={submitForm.due_date} onChange={e => setSubmitForm(p => ({ ...p, due_date: e.target.value }))} /></div>
            <p className="text-xs text-gray-500 bg-blue-50 p-2 rounded">Auto-assigned to all students in grade <strong>{submitForm.grade_level || '—'}</strong> mapped to you.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-submit-btn" onClick={handleSubmitAssignment} disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}{submitting ? 'Creating...' : 'Create & Assign'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Paper Dialog */}
      <Dialog open={!!viewPaper} onOpenChange={() => setViewPaper(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{viewPaper?.title}</DialogTitle></DialogHeader>
          {viewPaper?.question_paper && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm grid grid-cols-4 gap-2">
                <div>Subject: <strong>{viewPaper.subject}</strong></div>
                <div>Grade: <strong>{viewPaper.grade_level}</strong></div>
                <div>Total: <strong>{viewPaper.total_marks} marks</strong></div>
                <div>Due: <strong>{viewPaper.due_date ? new Date(viewPaper.due_date).toLocaleDateString() : 'N/A'}</strong></div>
              </div>
              {(viewPaper.question_paper.sections || []).map((s, si) => (
                <div key={si} className="border rounded-lg">
                  <div className="bg-blue-50 px-4 py-2 font-semibold text-sm text-blue-800">{s.name} ({s.questions?.length} Q)</div>
                  <div className="p-3 space-y-2">{(s.questions || []).map((q, qi) => (
                    <div key={qi} className="p-2 bg-gray-50 rounded text-sm"><span className="text-gray-400 mr-2">Q{q.number || qi+1}.</span>{q.text}<div className="flex gap-2 mt-1 text-xs"><Badge variant="outline">{q.type}</Badge><span>{q.marks} marks</span>{q.correct_answer && <span className="text-green-600">Ans: {q.correct_answer}</span>}</div></div>
                  ))}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Submission Dialog */}
      <Dialog open={!!reviewSubmission} onOpenChange={() => setReviewSubmission(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> Review: {reviewSubmission?.student_name}</DialogTitle></DialogHeader>
          {reviewSubmission && (
            <div className="space-y-4">
              <Card className="p-3 bg-blue-50 border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{reviewSubmission.assignment_title}</span>
                  <Badge className={`${(reviewSubmission.score_percentage || 0) >= 70 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>AI Score: {reviewSubmission.earned_marks ?? 0}/{reviewSubmission.total_marks || 0} ({reviewSubmission.score_percentage ?? 0}%)</Badge>
                </div>
              </Card>
              <div>
                <Label>Your Final Score (modify if needed)</Label>
                <Input type="number" value={reviewForm.final_total} onChange={e => setReviewForm({...reviewForm, final_total: parseFloat(e.target.value) || 0})} min={0} max={reviewSubmission.total_marks || 100} data-testid="review-score-input" />
              </div>
              <div>
                <Label>Feedback to Student *</Label>
                <Textarea value={reviewForm.feedback} onChange={e => setReviewForm({...reviewForm, feedback: e.target.value})} placeholder="Provide detailed feedback, areas of improvement, encouragement..." rows={4} data-testid="review-feedback-input" />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={reviewForm.send_to_student} onChange={e => setReviewForm({...reviewForm, send_to_student: e.target.checked})} /> Send to Student</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={reviewForm.send_to_parent} onChange={e => setReviewForm({...reviewForm, send_to_parent: e.target.checked})} /> Send to Parent</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewSubmission(null)}>Cancel</Button>
            <Button onClick={handleReviewSubmit} disabled={submittingReview} data-testid="submit-review-btn">
              {submittingReview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              {submittingReview ? 'Submitting...' : 'Submit Review & Send Feedback'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignmentUploadManager;
